import { InvalidApprovedInput, InvalidApprovedInputs } from '../types/approvals-table.types';
import { getApprovalMemberRowId, getApprovalProjectRowId } from './approval-row-model.helper';

/** Record key of one approved input. Entry + field, because a row has two of them. */
export const invalidApprovedInputKey = (
  entryId: number,
  field: InvalidApprovedInput['field']
): string => `${entryId}:${field}`;

/** True when anything on screen currently holds unparseable text. */
export const hasAnyInvalidApprovedInput = (inputs: InvalidApprovedInputs): boolean =>
  Object.keys(inputs).length > 0;

const inputsOf = (inputs: InvalidApprovedInputs): InvalidApprovedInput[] => Object.values(inputs);

/** The unparseable text a given input holds, or undefined when it parses fine. */
export const getInvalidApprovedInputValue = (
  inputs: InvalidApprovedInputs,
  entryId: number,
  field: InvalidApprovedInput['field']
): string | undefined => inputs[invalidApprovedInputKey(entryId, field)]?.rawValue;

/** True when either of a worklog row's two approved inputs is unparseable. */
export const isEntryApprovedInputInvalid = (
  inputs: InvalidApprovedInputs,
  entryId: number
): boolean => inputsOf(inputs).some(input => input.entryId === entryId);

/**
 * True when any worklog under this member row holds unparseable text.
 *
 * Matches on the canonical row id (the single source of truth for identity) rather than
 * walking the tree, so a COLLAPSED member still reports the problem — which is the whole
 * point of the warning.
 */
export const hasInvalidApprovedInputForMemberRow = (
  inputs: InvalidApprovedInputs,
  memberRowId: string
): boolean =>
  inputsOf(inputs).some(
    input => getApprovalMemberRowId(input.projectId, input.memberId) === memberRowId
  );

/** True when any worklog under this project row holds unparseable text (collapsed or not). */
export const hasInvalidApprovedInputForProjectRow = (
  inputs: InvalidApprovedInputs,
  projectRowId: string
): boolean =>
  inputsOf(inputs).some(input => getApprovalProjectRowId(input.projectId) === projectRowId);

export const hasInvalidApprovedInputForAggregateRow = (
  inputs: InvalidApprovedInputs,
  rowId: string
): boolean =>
  hasInvalidApprovedInputForMemberRow(inputs, rowId) ||
  hasInvalidApprovedInputForProjectRow(inputs, rowId);
